import { useEffect } from "react";
import Footer from "@/components/landing/layout/Footer";
import Header from "@/components/landing/layout/Header";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";

const claimExamples = [
  {
    scenario: "Car seat cover torn by pet claws",
    covered: "Yes",
    reason: "Pet-related accidental damage covered",
  },
  {
    scenario: "Car floor mat stained by spilled liquid",
    covered: "Yes",
    reason: "Accidental spill damage covered",
  },
  {
    scenario: "Organizer tray cracked due to accidental drop",
    covered: "Yes",
    reason: "Accidental drop impact covered",
  },
  {
    scenario: "Dashboard cover scratched during installation",
    covered: "Yes",
    reason: "Accidental handling damage covered",
  },
  {
    scenario: "Car cushion compressed and damaged when heavy object falls on it",
    covered: "Yes",
    reason: "Accidental impact during normal use covered",
  },
  {
    scenario: "Foot mat damage due to dirt accumulation over time",
    covered: "No",
    reason: "Normal wear & tear excluded",
  },
  {
    scenario: "Car cover fabric faded due to sun exposure",
    covered: "No",
    reason: "Environmental and wear & tear excluded",
  },
  {
    scenario: "Car organizer broken during misuse or intentional bending",
    covered: "No",
    reason: "Misuse excluded",
  },
];

const definitions = [
  ["Customer", "Person purchasing Autofurnish product with Protection Plan"],
  ["Merchant", "Autofurnish"],
  ["Protega", "EmbedMike India Pvt. Ltd., Plan administrator"],
  ["Product", "Eligible Autofurnish item covered under this Plan"],
  [
    "Accidental Damage",
    "Sudden, unintended events causing physical damage (tears, spills, pet damage, impacts)",
  ],
  ["Policy Period", "12 or 24 months from delivery date, based on selected Plan"],
  ["Sum Insured", "Invoice value of the product (excluding taxes/fees)"],
  ["Claim Limit", "One valid claim per product, up to Sum Insured"],
];

const ProtegaProductProtectionPlan: React.FC = () => {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-black">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        <section className="tw-bg-white tw-font-mori tw-max-w-5xl tw-mx-auto tw-px-6 tw-py-12 tw-text-[#1C1E21] tw-leading-relaxed">
          <h1 className="md:tw-text-3xl tw-text-2xl tw-font-bold tw-mb-1">
            Protega Product Protection Plan — Autofurnish
          </h1>
          <p className="tw-text-sm tw-mb-8">Terms and Conditions</p>

          {/* 1. Plan Introduction */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              1. Plan Introduction
            </h2>
            <ol className="tw-list-decimal tw-list-inside tw-text-sm tw-space-y-2">
              <li>
                The Protega Product Protection Plan provides comprehensive
                coverage for eligible Autofurnish products (car seat covers,
                foot mats, organisers, cushions, related accessories) for
                accidental damage during normal domestic or vehicle use.
              </li>
              <li>
                This Plan is an optional, paid service contract and is not an
                insurance policy nor a replacement for manufacturer warranty.
              </li>
              <li>
                Coverage includes accidental spills, tears, pet damage, and
                sudden impacts.
              </li>
              <li>Administered by EmbedMike India Pvt. Ltd. (“Protega”).</li>
              <li>Merchant: Autofurnish.</li>
              <li>Governing Law: Laws of India; Jurisdiction: Exclusive Mumbai courts.</li>
            </ol>
          </section>

          {/* 2. Customer Responsibilities */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              2. Customer Responsibilities
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>Retain purchase invoice and Protection Plan certificate.</li>
              <li>File claims as soon as practicable after a damage incident.</li>
              <li>
                Avoid unauthorised repairs or modifications; these may void
                coverage.
              </li>
              <li>Maintain products per manufacturer care instructions.</li>
              <li>Handle products responsibly to prevent non-accidental damage.</li>
            </ul>
          </section>

          {/* 3. How to File a Claim */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              3. How to File a Claim
            </h2>
            <div className="tw-text-sm tw-space-y-2">
              <p>
                <strong>Contact Methods:</strong> Email:{" "}
                <a className="tw-underline" href="mailto:support@protega.ai">
                  support@protega.ai
                </a>{" "}
                | Web:{" "}
                <a
                  className="tw-underline"
                  href="https://www.protega.ai/customer-login"
                >
                  www.protega.ai/customer-login
                </a>
              </p>
              <p>
                <strong>Required Documents:</strong> Protection Plan
                certificate, purchase invoice, clear photos/videos of damage,
                detailed incident description (when/how).
              </p>
              <p>
                <strong>Protega Remedies (at discretion):</strong> Repair with
                genuine parts, replacement with same/comparable product, or
                voucher/credit equal to invoice value.
              </p>
              <p>
                <strong>Claim Limit:</strong> One valid claim per product during
                the Policy Period.
              </p>
            </div>
          </section>

          {/* 4. Definitions */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              4. Definitions
            </h2>
            <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
              <table className="tw-w-full md:tw-text-sm tw-text-xs tw-table-fixed">
                <thead className="tw-bg-gray-100">
                  <tr>
                    <th className="tw-p-3 tw-border-r tw-text-left tw-w-1/3">
                      Term
                    </th>
                    <th className="tw-p-3 tw-text-left">Definition</th>
                  </tr>
                </thead>
                <tbody>
                  {definitions.map(([term, def], i) => (
                    <tr
                      key={i}
                      className={i % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50"}
                    >
                      <td className="tw-p-3 tw-border-r">{term}</td>
                      <td className="tw-p-3">{def}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 5. Eligibility */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              5. Eligibility
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>
                Only products purchased from Autofurnish.com with Product
                Protection selected are eligible.
              </li>
              <li>
                Coverage begins on delivery date and lasts for the chosen Policy
                Period (12 or 24 months).
              </li>
              <li>Valid within India only.</li>
            </ul>
          </section>

          {/* 6. What is Covered */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              6. What is Covered
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>Accidental tearing, punctures, crushing, or breakage.</li>
              <li>Liquid spills or stains affecting usability.</li>
              <li>Pet damage (scratches, chewing, tearing).</li>
              <li>Accidental drops or impacts causing visible damage.</li>
              <li>
                Non-consumable accessories sold with the product (organisers,
                attachable components).
              </li>
              <li>Coverage is single-event per claim up to the invoice value.</li>
            </ul>
          </section>

          {/* 7. What is Not Covered */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              7. What is Not Covered
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>Manufacturer defects (covered under warranty).</li>
              <li>Normal wear & tear, fading, peeling, loosening.</li>
              <li>Cosmetic scratches/dents not affecting functionality.</li>
              <li>Pre-existing damage prior to Plan start date.</li>
              <li>
                Intentional damage, misuse, mishandling, gross negligence.
              </li>
              <li>Use outside normal domestic or vehicle settings.</li>
              <li>
                Consumables (filters, pads, liners) unless damaged during a
                covered event.
              </li>
              <li>
                Environmental or natural damage (fire, flood, earthquake, pests).
              </li>
              <li>
                Damage due to electrical or power surges, voltage fluctuations,
                short circuits.
              </li>
              <li>Theft, burglary, or loss after successful delivery.</li>
              <li>Unauthorised repairs, modifications, or non-genuine parts.</li>
              <li>
                Any loss/damage from exposure to fire, excessive heat, smoke, or
                burns (including cigarette burns).
              </li>
              <li>
                Failure to follow product care instructions; claims after Policy
                Period.
              </li>
            </ul>
          </section>

          {/* 8. Service Procedures & Limit of Liability */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              8. Service Procedures & Limit of Liability
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>Protega evaluates each claim and verifies accidental damage.</li>
              <li>
                Repairs/replacements done by authorised providers using genuine
                parts.
              </li>
              <li>
                Voucher/credit issued when repair/replacement is not feasible.
              </li>
              <li>
                Maximum liability capped at invoice value; Plan ends once claim
                resolved.
              </li>
              <li>
                Protega may request damaged product/parts for inspection and
                retains subrogation rights.
              </li>
              <li>
                Protega will maintain transparent communication during the claim
                process.
              </li>
            </ul>
          </section>

          {/* 9. Cancellation */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              9. Cancellation
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>Cancel within 30 days if no claim filed — full refund.</li>
              <li>
                After 30 days or post-claim, Plan is non-cancellable and
                non-refundable.
              </li>
              <li>
                Protega may cancel for fraud, misrepresentation, or non-payment.
              </li>
            </ul>
          </section>

          {/* 10. Conditions */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              10. Conditions
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>Renewal not available; purchase new Plan after expiry.</li>
              <li>Non-transferable; linked to original purchaser.</li>
              <li>Valid only within India.</li>
              <li>Customers must follow manufacturer care instructions.</li>
              <li>
                Protega reserves recovery rights from responsible third parties.
              </li>
            </ul>
          </section>

          {/* 11. Legal Disclosures */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              11. Legal Disclosures
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>
                Disputes resolved via arbitration under Arbitration &amp;
                Conciliation Act, 1996.
              </li>
              <li>Plan + purchase confirmation = entire agreement.</li>
              <li>Severability preserved for invalid clauses.</li>
              <li>Customer data processed as per Protega Privacy Policy.</li>
              <li>
                Protega &amp; Autofurnish not liable for indirect or
                consequential losses.
              </li>
              <li>
                Protega may amend policies subject to regulatory compliance.
              </li>
            </ul>
          </section>

          {/* 12. Claim Examples */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              12. Filing a Claim — Examples
            </h2>
            <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
              <table className="tw-w-full md:tw-text-sm tw-text-xs tw-table-fixed">
                <thead className="tw-bg-gray-100">
                  <tr>
                    <th className="tw-p-3 tw-border-r tw-text-left md:tw-w-1/2">
                      Scenario
                    </th>
                    <th className="tw-p-3 tw-border-r tw-text-left">Covered?</th>
                    <th className="tw-p-3 tw-text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {claimExamples.map((ex, i) => (
                    <tr
                      key={i}
                      className={i % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50"}
                    >
                      <td className="tw-p-3 tw-border-r">{ex.scenario}</td>
                      <td className="tw-p-3 tw-border-r">{ex.covered}</td>
                      <td className="tw-p-3">{ex.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 13. FAQs */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">13. FAQ</h2>
            <div className="tw-text-sm tw-space-y-2">
              <p>
                Q1: Is this Plan the same as the manufacturer’s warranty? — A1:
                No. Warranty covers manufacturing defects; this Plan covers
                accidental damage such as spills, pet damage, or impacts.
              </p>
              <p>
                Q2: How long is my product protected? — A2: Coverage begins on
                delivery date and lasts 12 or 24 months, depending on the Plan.
              </p>
              <p>
                Q3: How many claims can I make? — A3: Only one valid claim per
                product during the Policy Period, capped at invoice value.
              </p>
              <p>
                Q4: What documents are required for a claim? — A4: Purchase
                invoice, Plan certificate, and photos/videos showing damage.
              </p>
              <p>
                Q5: What happens if repair is not possible? — A5: Replacement
                with same/comparable product or voucher/credit equal to invoice
                value.
              </p>
              <p>
                Q6: Can I cancel the Plan? — A6: Yes, within 30 days if no claim
                is filed. Afterwards, non-refundable.
              </p>
              <p>
                Q7: Are accessories covered? — A7: Non-consumable accessories
                sold together with product are covered; consumables excluded
                unless damaged in a covered event.
              </p>
              <p>
                Q8: Do I need to register separately? — A8: No. Plan is
                automatically registered at checkout.
              </p>
              <p>
                Q9: How long will my claim take? — A9: Claims acknowledged
                within 24 hours; resolved within 5–7 working days after
                inspection.
              </p>
              <p>
                Q10: Are pet accidents covered? — A10: Yes, accidental pet damage
                during normal use is included.
              </p>
              <p>
                Q11: Are liquid spills covered? — A11: Yes, if the spill causes
                functional damage during normal use.
              </p>
            </div>
          </section>

          {/* 14. Service Commitment */}
          <section className="tw-mb-6">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              14. Service Commitment
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
              <li>Policy certificate emailed within 24 hours of purchase.</li>
              <li>Claims acknowledged within 24 hours.</li>
              <li>Inspection and verification completed promptly.</li>
              <li>
                Pickup of damaged products arranged within 48 hours
                post-approval.
              </li>
              <li>Repair or replacement completed within 7–10 working days.</li>
              <li>Replacement units delivered with tracking updates.</li>
              <li>SMS/email updates for claim progress at every stage.</li>
              <li>Dedicated support team available for queries and guidance.</li>
            </ul>
          </section>

          {/* 15. Contact Us */}
          <section className="tw-mb-12">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              15. Contact Us
            </h2>
            <div className="tw-text-sm tw-space-y-2">
              <p>
                <strong>Email:</strong>{" "}
                <a className="tw-underline" href="mailto:support@protega.ai">
                  support@protega.ai
                </a>
              </p>
              <p>
                <strong>Web:</strong>{" "}
                <a className="tw-underline" href="https://www.protega.ai/customer-login">
                  www.protega.ai/customer-login
                </a>
              </p>
            </div>
          </section>

          <Link
            to={"/auto-furnish-productprotection"}
            className="tw-border-t tw-border-gray-200 tw-pt-6 tw-flex tw-items-center tw-text-sm tw-text-gray-800"
          >
            <ChevronLeft /> Back to Autofurnish Care
          </Link>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default ProtegaProductProtectionPlan;
